import React from 'react';
import { Autocomplete, MenuItem, Select, TextField } from '@mui/material';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import TableBasicLabel from '../../../../common/labels/TableBasicLabel/TableBasicLabel';
import CustomDayPicker from '../../../../common/CustomDayPicker/CustomDayPicker';
import AddGradientIcon from '../../../../common/Icons/AddGradientIcon';
import { ExpenseColumnType } from './types';
import TimesheetComment from '../../../timesheet/MainTable/TimesheetComment/TimesheetComment';
import ChooseFileWithCount from '../../../../common/buttons/ChooseFileWithCount/ChooseFileWithCount';
import ThreeDotMenu from '../../../../common/ThreeDotMenu/ThreeDotMenu';
import { generalEnums } from '../../../../../utils/enums';
import EditableLable from '../../../../common/labels/EditableLabel/EditableLable';
import { numberWithCommas } from '../../../../../utils/helpers';

const styleConfig = {
  checkboxes: { width: { min: '48px', max: '48px' } },
  location: { width: { min: '132px', max: '200px' } },
  worktype: { width: { min: '132px', max: '200px' } },
  comment: { width: { min: '180px', max: '180px' } },
  receipt: { width: { min: '200px', max: '200px' } },
  rate: { width: { min: '70px', max: '70px' } },
  date: { width: { min: '120px', max: '170px' } },
  threedot: { width: { min: '44px', max: '44px' } }
};

const ThreeDotContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: '24px'
}));

const ThreeDotItem = styled('p')(() => ({
  fontStyle: 'normal',
  fontWeight: 325,
  fontSize: '20px',
  lineHeight: '24px',
  color: '#77787b',
  cursor: 'pointer'
}));

const FooterCell = styled('div')(({ borderRadius = '0' }) => ({
  width: '100%',
  height: '100%',
  paddingTop: '8px',
  paddingBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius
}));

const textFieldStyles = (width) => ({
  width,
  height: '100%',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #F6F7F9',
    borderRadius: '10px',
    fontSize: '14px'
  },
  '& .MuiFormControl-root-MuiTextField-root': {
    width: '100%'
  },
  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
    paddingRight: '15px !important'
  },
  '& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input': {
    fontSize: '14px'
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    fontFamily: 'inherit'
  },
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    fontSize: '14px',
    color: '#003B58'
  },
  '& .MuiSvgIcon-root': {
    color: '#D1D3D4',
    fontSize: '18px',
    right: '15px'
  },
  color: '#003B58',
  fontSize: '14px',
  fontWeight: '325',
  lineHeight: '17px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
});

const labelStyle = {
  width: 'auto',
  height: '50px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  fontWeight: 325,
  fontSize: '14px',
  fontStyle: 'normal',
  color: '#003B58',
  paddingLeft: '15px'
};

const StyledLabel = styled('div')(({ width = 'auto' }) => ({ ...labelStyle, width }));

const columns = (t, categories, addHandler, changeHandler, deleteLine, isEdit, currencies, author = '') => {
  return [
    {
      id: '1',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.expenses.table.date')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 8px"
        />
      ),
      width: styleConfig.date.width,
      value: (data) => (
        <div
          style={{
            width: '100%',
            height: '100%'
          }}
        >
          {isEdit ? (
            <CustomDayPicker
              date={data?.Date}
              changeHandler={(newValue) => changeHandler(ExpenseColumnType.DATE, data.Id, newValue)}
              sx={textFieldStyles('150px')}
              dayFormat={generalEnums.DATEFORMAT}
            />
          ) : (
            <StyledLabel>{dayjs(data?.Date).format(generalEnums.DATEFORMAT)}</StyledLabel>
          )}
        </div>
      ),
      footer: () =>
        isEdit && (
          <div
            role="presentation"
            onClick={addHandler}
            style={{
              cursor: 'pointer',
              display: 'flex',
              flexWrap: 'nowrap',
              flexDirection: 'row',
              marginRight: 'auto',
              gap: '4px',
              color: '#A3B4C7'
            }}
          >
            <AddGradientIcon />
          </div>
        )
    },
    {
      id: '2',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.expenses.table.category')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 8px"
        />
      ),
      alignment: 'left',
      value: (data) =>
        isEdit ? (
          <div style={{ justifyContent: 'left', display: 'flex' }}>
            <Select
              value={data?.CategoryId}
              onChange={(e) => {
                changeHandler(ExpenseColumnType.CATEGORY, data.Id, e.target.value);
              }}
              sx={textFieldStyles('140px')}
              IconComponent={ExpandMoreIcon}
              displayEmpty
            >
              {categories?.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        ) : (
          <StyledLabel>{data?.Category?.Name}</StyledLabel>
        ),
      footer: () => ''
    },
    {
      id: '3',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.expenses.table.expenseamount')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 8px"
        />
      ),
      value: (data) =>
        isEdit ? (
          <div style={{ gap: '0px', display: 'flex', alignItems: 'center' }}>
            <EditableLable
              value={parseFloat(data?.ExpenseAmount)}
              changeHandler={(newValue) => {
                changeHandler(ExpenseColumnType.EXPENSE_AMOUNT, data.Id, newValue);
              }}
              width="80px"
              enableKeyUp
            />
            {data?.CategoryId === 5 ? (
              <StyledLabel>{t('common.general.miles')}</StyledLabel>
            ) : (
              <Autocomplete
                disableClearable
                value={data?.FxCurrency?.CurrencyName || data?.ExpenseCurrency?.CurrencyName}
                options={currencies}
                sx={{
                  paddingRight: '10px',
                  borderRadius: '10px 0 0 10px',
                  width: '80px',
                  ...textFieldStyles('100px', '0 10px 10px 0')
                }}
                popupIcon={<ExpandMoreIcon />}
                forcePopupIcon
                renderOption={(props, option) => (
                  <li {...props} key={option.key}>
                    {option.label}
                  </li>
                )}
                onChange={(event, newValue) => {
                  changeHandler(ExpenseColumnType.FX_CURRENCY, data.Id, newValue);
                }}
                renderInput={(params) => <TextField {...params} sx={textFieldStyles('80px')} />}
              />
            )}
          </div>
        ) : (
          <StyledLabel>{`${parseFloat(data?.ExpenseAmount).toFixed(2)} ${
            data.CategoryId === 5
              ? t('common.general.miles')
              : data?.FxCurrency?.CurrencyName || data?.ExpenseCurrency?.CurrencyName
          }`}</StyledLabel>
        ),
      footer: () => ''
    },
    {
      id: '4',
      name: () => <TableBasicLabel text={t('dashboard.expenses.table.rate')} fontColor="white" textAlign="center" />,
      width: styleConfig.rate.width,
      value: (data) =>
        isEdit ? (
          <div style={{ gap: '10px', display: 'flex' }}>
            <EditableLable
              placeholder="Rate"
              value={data?.FxAmount || 1}
              disabled={
                data?.CategoryId !== 5 &&
                (data?.FxCurrency?.CurrencyName
                  ? data?.ExpenseCurrency?.CurrencyName === data?.FxCurrency?.CurrencyName
                  : true)
              }
              changeHandler={(newValue) => {
                changeHandler(ExpenseColumnType.RATE, data.Id, newValue);
              }}
              width="50px"
              enableKeyUp
            />
          </div>
        ) : (
          <StyledLabel>
            {data?.ExpenseCurrency?.CurrencyName === data?.FxCurrency?.CurrencyName ? 1 : data?.FxAmount || 1}
          </StyledLabel>
        ),
      footer: () => ''
    },
    {
      id: '5',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.expenses.table.claimamount')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 8px"
        />
      ),
      value: (data) => (
        <div style={{ display: 'flex', justifyContent: 'left', width: '100%' }}>
          <StyledLabel>
            {numberWithCommas(parseFloat(data?.TotalAmount).toFixed(2))} {data?.ExpenseCurrency?.CurrencyName}
          </StyledLabel>
        </div>
      ),
      footer: () => ''
    },
    {
      id: '6',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.expenses.table.comment')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 8px"
        />
      ),
      width: styleConfig.comment.width,
      value: (data) => (
        <TimesheetComment
          disabled={!isEdit}
          comment={data?.Comment}
          changeHandler={(newValue) => changeHandler(ExpenseColumnType.COMMENT, data.Id, newValue)}
          author={author}
        />
      ),
      footer: () => ''
    },
    {
      id: '7',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.expenses.table.receipt')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 8px"
        />
      ),
      width: styleConfig.receipt.width,
      value: (data) => (
        <ChooseFileWithCount
          actionHandler={(isChecked) => changeHandler(ExpenseColumnType.RECEIPT, data.Id, isChecked)}
          fileCounter={data?.ExpenseAttachments?.length || 0}
          isChecked={data?.NoReceiptCheck}
          isEdit={isEdit}
        />
      ),
      footer: () => ''
    },
    {
      id: '12',
      width: styleConfig.threedot.width,
      name: () => '',
      value: (data) => {
        return (
          isEdit && (
            <ThreeDotMenu menuComponent={<MoreVertOutlinedIcon sx={{ color: '#A3B4C7', fontSize: '18px' }} />}>
              <ThreeDotContainer>
                <ThreeDotItem onClick={() => deleteLine(data?.Id)}>
                  {t('dashboard.timesheet.table.deleteline')}
                </ThreeDotItem>
              </ThreeDotContainer>
            </ThreeDotMenu>
          )
        );
      },
      footer: () => <FooterCell borderRadius="0 10px 10px 0" />
    }
  ];
};

export { columns };
